// add-primary-key / drop-primary-key edit planner (v2 Slice B1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B1.
//
// Both ops append a fresh ALTER TABLE statement after the source statement
// (DSE2-7: no coalescing by default; opt-in coalescing is slice H1). Source
// path may be either ALTER TABLE or CREATE TABLE — the synthesized statement
// targets the same table named by the source structure.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, PlannedByteEdit, PrimaryKeyDef } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { sourceSpellingOrigin } from './columnEdit.alterTable';
import {
  type ColumnListOrigins,
  type DecodedColumnList,
  decodeTypedColumnList,
} from './existingNameEvidence';
import {
  synthesizeAddPrimaryKey,
  synthesizeDropPrimaryKey,
} from './dialect.primaryKey';

type PrimaryKeyEdit = Extract<DdlEdit, { op: 'add-primary-key' | 'drop-primary-key' }>;

export function planPrimaryKeyEdit(
  ctx: DdlEditCtx,
  edit: PrimaryKeyEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  if (!table) {
    throw new Error(
      `applyDdlEdit: cannot apply ${edit.op} edit to ${ctx.structure.kind} statement`,
    );
  }
  const synth = edit.op === 'add-primary-key'
    ? synthesizeAdd(ctx, table, edit.primaryKey)
    : synthesizeDropPrimaryKey(ctx.engine, table, edit.constraintName, edit.cascade);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:alter-table`,
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}

/** Decode the caller's column lists, then hand the synthesizer names plus the
 *  origin each element answers with. Both halves have to happen HERE, at the
 *  edit entry point, because they are one decision: the delimiters a user
 *  typed in the VQE's free-text column box are simultaneously the thing to
 *  strip from the name and the reason the stripped bytes are the catalog's.
 *
 *  A spelling that asserted NOTHING falls through to the source statement,
 *  which speaks for its own table's columns whether it is an ALTER TABLE or a
 *  CREATE TABLE — this constraint is emitted against `pickTable(structure)`,
 *  that same table, so no relation check is needed here (an index or a foreign
 *  key's ref side, which CAN name another relation, is gated on its own).
 *
 *  KEY and INCLUDE answer SEPARATELY, and each answers BY POSITION. They are
 *  two lists on one edit, not one list in two halves: a lookup shared between
 *  them turns a typed `"Id"` in INCLUDE into the KEY element's answer, and the
 *  key then goes out on `Id` where `id` was meant. Nothing downstream catches
 *  that — PostgreSQL ACCEPTS an INCLUDE that overlaps its own key, which is
 *  why the corpus entry `uq-key-vs-include-dup` has to SEED the two columns
 *  differently (`id` two distinct values, `Id` the same value twice) to make
 *  the unique index build on the intended column and be refused on the wrong
 *  one. Within one list the position is the only thing that identifies an
 *  element — `Id` and `"Id"` decode to the same bytes and name different
 *  columns. */
function synthesizeAdd(
  ctx: DdlEditCtx,
  table: QualifiedName,
  pk: PrimaryKeyDef,
): string {
  const keyCols = decodeTypedColumnList(ctx.engine, pk.columns);
  const includeCols = pk.includeColumns === undefined
    ? undefined
    : decodeTypedColumnList(ctx.engine, pk.includeColumns);
  const decoded: PrimaryKeyDef = {
    ...pk,
    columns: keyCols.columns,
    ...(includeCols !== undefined ? { includeColumns: includeCols.columns } : {}),
  };
  return synthesizeAddPrimaryKey(ctx.engine, table, decoded, {
    quoteName: table.quoted,
    columnNameOrigin: originsOf(ctx, keyCols),
    ...(includeCols !== undefined
      ? { includeColumnNameOrigin: originsOf(ctx, includeCols) }
      : {}),
  });
}

/** One list's per-position answer: the element's own typed delimiters first,
 *  then the source statement's evidence about the name it decoded to. */
function originsOf(ctx: DdlEditCtx, list: DecodedColumnList): ColumnListOrigins {
  return (index) => list.typedOriginAt(index)
    ?? sourceSpellingOrigin(ctx.structure, list.columns[index]);
}
